import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultTailoredSupplyAppsFilter,
  defaultTailoredSupplyDomainsFilter,
  defaultTailoredSupplyListFilter,
  isTailoredSupplyEditorTab,
  isTailoredSupplySortProperty,
  TailoredSupplyEditorTab,
  TailoredSupplyLanguage,
  TailoredSupplySortDirection,
  type TailoredSupplyAppsFilter,
  type TailoredSupplyDomainsFilter,
  type TailoredSupplyListFilter,
} from './model';

export interface TailoredSupplyListRouteSearch {
  search?: string;
  page?: number;
  size?: number;
  sortProperty?: string | null;
  sortDirection?: string;
}

export interface TailoredSupplyCreateRouteSearch {
  section?: string;
  appsSearch?: string;
  appsPublishers?: string;
  appsPlatforms?: string;
  appsLanguages?: string;
  appsIabCategories?: string;
  appsPage?: number;
  appsSize?: number;
  appsSortProperty?: string | null;
  appsSortDirection?: string;
  domainsSearch?: string;
  domainsPublishers?: string;
  domainsIabCategories?: string;
  domainsPage?: number;
  domainsSize?: number;
  domainsSortProperty?: string | null;
  domainsSortDirection?: string;
}

export function tailoredSupplyListRouteSearchSchema(
  search: Record<string, unknown>,
): TailoredSupplyListRouteSearch {
  return compact({
    search: optionalTrimmedString(search['search']),
    page: optionalPositiveInteger(search['page']),
    size: optionalPositiveInteger(search['size']),
    sortProperty:
      search['sortProperty'] === null ? null : optionalTrimmedString(search['sortProperty']),
    sortDirection: optionalTrimmedString(search['sortDirection']),
  }) as TailoredSupplyListRouteSearch;
}

export function tailoredSupplyCreateRouteSearchSchema(
  search: Record<string, unknown>,
): TailoredSupplyCreateRouteSearch {
  return compact({
    section: optionalTrimmedString(
      search['section'] === undefined ? search['tab'] : search['section'],
    ),
    appsSearch: optionalTrimmedString(search['appsSearch']),
    appsPublishers: optionalTrimmedString(search['appsPublishers']),
    appsPlatforms: optionalTrimmedString(search['appsPlatforms']),
    appsLanguages: optionalTrimmedString(search['appsLanguages']),
    appsIabCategories: optionalTrimmedString(search['appsIabCategories']),
    appsPage: optionalPositiveInteger(search['appsPage']),
    appsSize: optionalPositiveInteger(search['appsSize']),
    appsSortProperty:
      search['appsSortProperty'] === null
        ? null
        : optionalTrimmedString(search['appsSortProperty']),
    appsSortDirection: optionalTrimmedString(search['appsSortDirection']),
    domainsSearch: optionalTrimmedString(search['domainsSearch']),
    domainsPublishers: optionalTrimmedString(search['domainsPublishers']),
    domainsIabCategories: optionalTrimmedString(search['domainsIabCategories']),
    domainsPage: optionalPositiveInteger(search['domainsPage']),
    domainsSize: optionalPositiveInteger(search['domainsSize']),
    domainsSortProperty:
      search['domainsSortProperty'] === null
        ? null
        : optionalTrimmedString(search['domainsSortProperty']),
    domainsSortDirection: optionalTrimmedString(search['domainsSortDirection']),
  }) as TailoredSupplyCreateRouteSearch;
}

export function parseTailoredSupplyListSearch(
  search: TailoredSupplyListRouteSearch,
): TailoredSupplyListFilter {
  return {
    name: search.search ?? defaultTailoredSupplyListFilter.name,
    page: search.page ? search.page - 1 : defaultTailoredSupplyListFilter.page,
    size: search.size ?? defaultTailoredSupplyListFilter.size,
    sortProperty:
      search.sortProperty === null ||
      (search.sortProperty && isTailoredSupplySortProperty(search.sortProperty))
        ? search.sortProperty
        : defaultTailoredSupplyListFilter.sortProperty,
    sortDirection: parseSortDirection(
      search.sortDirection,
      defaultTailoredSupplyListFilter.sortDirection,
    ),
  };
}

export function serializeTailoredSupplyListSearch(
  filter: TailoredSupplyListFilter,
): TailoredSupplyListRouteSearch {
  return compact({
    search: filter.name.trim() || undefined,
    page: filter.page ? filter.page + 1 : undefined,
    size: filter.size !== defaultTailoredSupplyListFilter.size ? filter.size : undefined,
    sortProperty:
      filter.sortProperty !== defaultTailoredSupplyListFilter.sortProperty
        ? filter.sortProperty
        : undefined,
    sortDirection:
      filter.sortDirection !== defaultTailoredSupplyListFilter.sortDirection
        ? filter.sortDirection
        : undefined,
  }) as TailoredSupplyListRouteSearch;
}

const tailoredSupplyLanguages: ReadonlySet<string> = new Set(Object.values(TailoredSupplyLanguage));

export function parseTailoredSupplyCreateSearch(search: TailoredSupplyCreateRouteSearch) {
  const tab =
    search.section && isTailoredSupplyEditorTab(search.section)
      ? search.section
      : TailoredSupplyEditorTab.General;
  const apps: TailoredSupplyAppsFilter = {
    ...defaultTailoredSupplyAppsFilter,
    searchTerm: search.appsSearch ?? '',
    publisherIds: commaValues(search.appsPublishers),
    platformIds: commaValues(search.appsPlatforms),
    languages: commaValues(search.appsLanguages).filter((value): value is TailoredSupplyLanguage =>
      tailoredSupplyLanguages.has(value),
    ),
    iabCategoryIds: commaValues(search.appsIabCategories),
    page: search.appsPage ? search.appsPage - 1 : 0,
    size: search.appsSize ?? defaultTailoredSupplyAppsFilter.size,
    sortProperty:
      search.appsSortProperty === null ||
      (search.appsSortProperty && isTailoredSupplySortProperty(search.appsSortProperty))
        ? search.appsSortProperty
        : defaultTailoredSupplyAppsFilter.sortProperty,
    sortDirection: parseSortDirection(
      search.appsSortDirection,
      defaultTailoredSupplyAppsFilter.sortDirection,
    ),
  };
  const domains: TailoredSupplyDomainsFilter = {
    ...defaultTailoredSupplyDomainsFilter,
    searchTerm: search.domainsSearch ?? '',
    publisherIds: commaValues(search.domainsPublishers),
    iabCategoryIds: commaValues(search.domainsIabCategories),
    page: search.domainsPage ? search.domainsPage - 1 : 0,
    size: search.domainsSize ?? defaultTailoredSupplyDomainsFilter.size,
    sortProperty:
      search.domainsSortProperty === null ||
      (search.domainsSortProperty && isTailoredSupplySortProperty(search.domainsSortProperty))
        ? search.domainsSortProperty
        : defaultTailoredSupplyDomainsFilter.sortProperty,
    sortDirection: parseSortDirection(
      search.domainsSortDirection,
      defaultTailoredSupplyDomainsFilter.sortDirection,
    ),
  };
  return { tab, apps, domains };
}

export function serializeTailoredSupplyCreateSearch(
  tab: TailoredSupplyEditorTab,
  apps: TailoredSupplyAppsFilter,
  domains: TailoredSupplyDomainsFilter,
): TailoredSupplyCreateRouteSearch {
  return compact({
    section: tab === TailoredSupplyEditorTab.General ? undefined : tab,
    appsSearch: apps.searchTerm.trim() || undefined,
    appsPublishers: joined(apps.publisherIds),
    appsPlatforms: joined(apps.platformIds),
    appsLanguages: joined(apps.languages),
    appsIabCategories: joined(apps.iabCategoryIds),
    appsPage: apps.page ? apps.page + 1 : undefined,
    appsSize: apps.size !== defaultTailoredSupplyAppsFilter.size ? apps.size : undefined,
    appsSortProperty:
      apps.sortProperty !== defaultTailoredSupplyAppsFilter.sortProperty
        ? apps.sortProperty
        : undefined,
    appsSortDirection:
      apps.sortDirection !== defaultTailoredSupplyAppsFilter.sortDirection
        ? apps.sortDirection
        : undefined,
    domainsSearch: domains.searchTerm.trim() || undefined,
    domainsPublishers: joined(domains.publisherIds),
    domainsIabCategories: joined(domains.iabCategoryIds),
    domainsPage: domains.page ? domains.page + 1 : undefined,
    domainsSize:
      domains.size !== defaultTailoredSupplyDomainsFilter.size ? domains.size : undefined,
    domainsSortProperty:
      domains.sortProperty !== defaultTailoredSupplyDomainsFilter.sortProperty
        ? domains.sortProperty
        : undefined,
    domainsSortDirection:
      domains.sortDirection !== defaultTailoredSupplyDomainsFilter.sortDirection
        ? domains.sortDirection
        : undefined,
  }) as TailoredSupplyCreateRouteSearch;
}

function parseSortDirection(value: string | undefined, fallback: TailoredSupplySortDirection) {
  return value === TailoredSupplySortDirection.Ascending ||
    value === TailoredSupplySortDirection.Descending
    ? value
    : fallback;
}

function commaValues(value: string | undefined) {
  return value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
}

function joined(values: readonly string[]) {
  return values.length ? values.join(',') : undefined;
}

function compact<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}
